import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import '../styles/Pages.css';
import { formatDate } from '../utils/formatters';


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`
});

const fetchReport = async (endpoint, params = {}) => {
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([_, v]) => v != null && v !== '')
  ).toString();
  
  const url = `${API_URL}/financial-reports/${endpoint}${queryString ? '?' + queryString : ''}`;
  const response = await fetch(url, { headers: getAuthHeaders() });
  
  if (!response.ok) throw new Error('Rapor yüklenemedi');
  return response.json();
};

function Reports() {
  const [activeReport, setActiveReport] = useState('payment-summary');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: 1, label: 'Ocak' }, { value: 2, label: 'Şubat' },
    { value: 3, label: 'Mart' }, { value: 4, label: 'Nisan' },
    { value: 5, label: 'Mayıs' }, { value: 6, label: 'Haziran' },
    { value: 7, label: 'Temmuz' }, { value: 8, label: 'Ağustos' },
    { value: 9, label: 'Eylül' }, { value: 10, label: 'Ekim' },
    { value: 11, label: 'Kasım' }, { value: 12, label: 'Aralık' }
  ];

  const reportTypes = [
    { id: 'payment-summary', label: 'Ödeme Özeti', description: 'Yöntem bazlı tahsilat' },
    { id: 'monthly-collections', label: 'Aylık Tahsilat', description: 'Ay ay tahsilatlar' },
    { id: 'kdv-summary', label: 'KDV Raporu', description: 'Fatura ve KDV detayı' },
    { id: 'daily-collections', label: 'Günlük Tahsilat', description: 'Gün bazlı detay' },
    { id: 'receivables', label: 'Alacaklar', description: 'Bekleyen ödemeler' },
    { id: 'comparison', label: 'Karşılaştırma', description: 'Sipariş vs Tahsilat' }
  ];

  const buildParams = () => {
    let params = {};
    
    switch (activeReport) {
      case 'monthly-collections':
      case 'comparison':
        params = { year: selectedYear };
        break;
        
      case 'receivables':
        if (selectedYear) params.year = selectedYear;
        if (selectedMonth) params.month = selectedMonth;
        break;
        
      case 'daily-collections':
        // Günlük tahsilat için tarih aralığı hesapla
        if (dateFrom && dateTo) {
          params = { date_from: dateFrom, date_to: dateTo };
        } else if (selectedYear && selectedMonth) {
          // Yıl ve ay seçiliyse tarih aralığına çevir
          const year = parseInt(selectedYear);
          const month = parseInt(selectedMonth);
          const firstDay = `${year}-${String(month).padStart(2, '0')}-01`;
          const lastDayDate = new Date(year, month, 0);
          const lastDay = `${year}-${String(month).padStart(2, '0')}-${String(lastDayDate.getDate()).padStart(2, '0')}`;
          params = { date_from: firstDay, date_to: lastDay };
        } else if (selectedYear) {
          params = { date_from: `${selectedYear}-01-01`, date_to: `${selectedYear}-12-31` };
        }
        break;
        
      case 'payment-summary':
      case 'kdv-summary':
      default:
        if (dateFrom && dateTo) {
          params = { date_from: dateFrom, date_to: dateTo };
        } else if (selectedYear && selectedMonth) {
          params = { year: selectedYear, month: selectedMonth };
        } else if (selectedYear) {
          params = { year: selectedYear };
        }
        break;
    }
    
    return params;
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const data = await fetchReport(activeReport, params);
      setReportData(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Rapor yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [activeReport]);

  const clearDateFilters = () => {
    setDateFrom('');
    setDateTo('');
  };

  const handlePrint = () => window.print();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(value || 0);
  };


  const renderFilters = () => {
    if (activeReport === 'monthly-collections' || activeReport === 'comparison') {
      return (
        <div className="report-filters no-print">
          <div className="filter-group">
            <label>Yıl</label>
            <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button className="btn-primary" onClick={loadReport} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Raporu Getir'}
          </button>
        </div>
      );
    }
    
    if (activeReport === 'receivables') {
      return (
        <div className="report-filters no-print">
          <div className="filter-group">
            <label>Yıl</label>
            <select value={selectedYear || ''} onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : '')}>
              <option value="">Tümü</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Ay</label>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} disabled={!selectedYear}>
              <option value="">Tümü</option>
              {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <button className="btn-primary" onClick={loadReport} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Raporu Getir'}
          </button>
        </div>
      );
    }
    
    // daily-collections, payment-summary, kdv-summary için
    return (
      <div className="report-filters no-print">
        <div className="filter-group">
          <label>Yıl</label>
          <select value={selectedYear} onChange={(e) => { setSelectedYear(parseInt(e.target.value)); setDateFrom(''); setDateTo(''); }} disabled={!!(dateFrom || dateTo)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Ay</label>
          <select value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); setDateFrom(''); setDateTo(''); }} disabled={!!(dateFrom || dateTo)}>
            <option value="">Tümü</option>
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div className="filter-divider">veya</div>
        <div className="filter-group">
          <label>Başlangıç</label>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setSelectedMonth(''); }} />
        </div>
        <div className="filter-group">
          <label>Bitiş</label>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setSelectedMonth(''); }} />
        </div>
        {(dateFrom || dateTo) && (
          <button className="btn-secondary btn-small" onClick={clearDateFilters}>Temizle</button>
        )}
        <button className="btn-primary" onClick={loadReport} disabled={loading}>
          {loading ? 'Yükleniyor...' : 'Raporu Getir'}
        </button>
      </div>
    );
  };

  const renderPaymentSummary = () => {
    if (!reportData) return null;
    
    return (
      <div className="report-content">
        <div className="report-summary-cards">
          <div className="summary-card primary">
            <h4>Toplam Tahsilat</h4>
            <p className="big-number">{formatCurrency(reportData.total_collected)}</p>
            <span>{reportData.payment_count} ödeme</span>
          </div>
        </div>
        
        <h3 className="section-title">Ödeme Yöntemine Göre Dağılım</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Ödeme Yöntemi</th>
              <th>Adet</th>
              <th>Tutar</th>
              <th>Oran</th>
            </tr>
          </thead>
          <tbody>
            {reportData.by_method?.map((item, idx) => (
              <tr key={idx}>
                <td><strong>{item.method_label}</strong></td>
                <td>{item.count}</td>
                <td>{formatCurrency(item.total)}</td>
                <td>
                  <div className="progress-bar-container">
                    <div className="progress-bar" style={{width: `${item.percentage}%`}}></div>
                    <span>{item.percentage}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMonthlyCollections = () => {
    if (!reportData) return null;
    
    return (
      <div className="report-content">
        <div className="report-summary-cards">
          <div className="summary-card primary">
            <h4>{reportData.year} Yılı Toplam</h4>
            <p className="big-number">{formatCurrency(reportData.total_year)}</p>
          </div>
        </div>
        
        <h3 className="section-title">Aylık Detay</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Ay</th>
              <th>Ödeme Sayısı</th>
              <th>Tahsilat</th>
              <th>Grafik</th>
            </tr>
          </thead>
          <tbody>
            {reportData.monthly?.map((item, idx) => {
              const maxValue = Math.max(...reportData.monthly.map(m => m.total_collected));
              const percentage = maxValue > 0 ? (item.total_collected / maxValue) * 100 : 0;
              
              return (
                <tr key={idx} className={item.total_collected === 0 ? 'row-muted' : ''}>
                  <td><strong>{item.month_name}</strong></td>
                  <td>{item.payment_count}</td>
                  <td>{formatCurrency(item.total_collected)}</td>
                  <td>
                    <div className="bar-chart">
                      <div className="bar" style={{width: `${percentage}%`}}></div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>TOPLAM</strong></td>
              <td><strong>{reportData.monthly?.reduce((a, b) => a + b.payment_count, 0)}</strong></td>
              <td><strong>{formatCurrency(reportData.total_year)}</strong></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderKdvSummary = () => {
    if (!reportData) return null;
    
    return (
      <div className="report-content">
        <div className="report-summary-cards">
          <div className="summary-card success">
            <h4>Faturalı Sipariş</h4>
            <p className="big-number">{formatCurrency(reportData.with_invoice?.grand_total)}</p>
            <span>{reportData.with_invoice?.order_count} sipariş</span>
          </div>
          <div className="summary-card warning">
            <h4>Toplam KDV</h4>
            <p className="big-number">{formatCurrency(reportData.with_invoice?.kdv_total)}</p>
          </div>
          <div className="summary-card info">
            <h4>Matrah</h4>
            <p className="big-number">{formatCurrency(reportData.with_invoice?.base_total)}</p>
          </div>
          <div className="summary-card secondary">
            <h4>Faturasız</h4>
            <p className="big-number">{formatCurrency(reportData.without_invoice?.total)}</p>
            <span>{reportData.without_invoice?.order_count} sipariş</span>
          </div>
        </div>
        
        <h3 className="section-title">KDV Oranına Göre Dağılım</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>KDV Oranı</th>
              <th>Sipariş</th>
              <th>Matrah</th>
              <th>KDV Tutarı</th>
              <th>Toplam</th>
            </tr>
          </thead>
          <tbody>
            {reportData.with_invoice?.by_kdv_rate?.map((item, idx) => (
              <tr key={idx}>
                <td><strong>{item.kdv_rate_label}</strong></td>
                <td>{item.order_count}</td>
                <td>{formatCurrency(item.base_total)}</td>
                <td>{formatCurrency(item.kdv_total)}</td>
                <td>{formatCurrency(item.grand_total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>TOPLAM</strong></td>
              <td><strong>{reportData.with_invoice?.order_count}</strong></td>
              <td><strong>{formatCurrency(reportData.with_invoice?.base_total)}</strong></td>
              <td><strong>{formatCurrency(reportData.with_invoice?.kdv_total)}</strong></td>
              <td><strong>{formatCurrency(reportData.with_invoice?.grand_total)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderDailyCollections = () => {
    if (!reportData) return null;
    
    return (
      <div className="report-content">
        <div className="report-summary-cards">
          <div className="summary-card primary">
            <h4>Dönem Toplamı</h4>
            <p className="big-number">{formatCurrency(reportData.total)}</p>
            <span>{formatDate(reportData.period?.date_from)} - {formatDate(reportData.period?.date_to)}</span>
          </div>
        </div>
        
        <h3 className="section-title">Günlük Detay</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Ödeme Sayısı</th>
              <th>Tahsilat</th>
            </tr>
          </thead>
          <tbody>
            {reportData.daily?.length > 0 ? (
              reportData.daily.map((item, idx) => (
                <tr key={idx}>
                  <td><strong>{formatDate(item.date)}</strong></td>
                  <td>{item.payment_count}</td>
                  <td>{formatCurrency(item.total_collected)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="empty-row">Bu dönemde tahsilat bulunmuyor</td>
              </tr>
            )}
          </tbody>
          {reportData.daily?.length > 0 && (
            <tfoot>
              <tr>
                <td><strong>TOPLAM</strong></td>
                <td><strong>{reportData.daily.reduce((a, b) => a + b.payment_count, 0)}</strong></td>
                <td><strong>{formatCurrency(reportData.total)}</strong></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  const renderReceivables = () => {
    if (!reportData) return null;
    
    const statusLabels = {
      'siparis_alindi': 'Sipariş Alındı',
      'uretimde': 'Üretimde',
      'teslim_edildi': 'Teslim Edildi'
    };
    
    return (
      <div className="report-content">
        <div className="report-summary-cards">
          <div className="summary-card danger">
            <h4>Toplam Alacak</h4>
            <p className="big-number">{formatCurrency(reportData.total_receivable)}</p>
            <span>{reportData.order_count} sipariş</span>
          </div>
        </div>
        
        <h3 className="section-title">Duruma Göre Dağılım</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Durum</th>
              <th>Sipariş</th>
              <th>Alacak</th>
            </tr>
          </thead>
          <tbody>
            {reportData.by_status?.map((item, idx) => (
              <tr key={idx}>
                <td><strong>{statusLabels[item.status] || item.status}</strong></td>
                <td>{item.count}</td>
                <td>{formatCurrency(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        <h3 className="section-title">En Yüksek Alacaklar (İlk 10)</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Sipariş #</th>
              <th>Müşteri</th>
              <th>Sipariş Tutarı</th>
              <th>Kalan Alacak</th>
              <th>Tarih</th>
            </tr>
          </thead>
          <tbody>
            {reportData.top_receivables?.length > 0 ? (
              reportData.top_receivables.map((item, idx) => (
                <tr key={idx}>
                  <td><strong>#{item.order_id}</strong></td>
                  <td>{item.customer_name}</td>
                  <td>{formatCurrency(item.total_price)}</td>
                  <td className="text-danger"><strong>{formatCurrency(item.remaining)}</strong></td>
                  <td>{formatDate(item.order_date)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="empty-row">Alacak bulunmuyor</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderComparison = () => {
    if (!reportData) return null;
    
    const totalRate = reportData.summary?.total_orders > 0 
      ? Math.round((reportData.summary?.total_payments / reportData.summary?.total_orders) * 100) 
      : 0;
    
    return (
      <div className="report-content">
        <div className="report-summary-cards">
          <div className="summary-card info">
            <h4>Toplam Sipariş</h4>
            <p className="big-number">{formatCurrency(reportData.summary?.total_orders)}</p>
            <span>Sipariş tutarları toplamı</span>
          </div>
          <div className="summary-card success">
            <h4>Toplam Tahsilat</h4>
            <p className="big-number">{formatCurrency(reportData.summary?.total_payments)}</p>
            <span>Tüm ödemeler toplamı</span>
          </div>
          <div className="summary-card danger">
            <h4>Toplam Alacak</h4>
            <p className="big-number">{formatCurrency(reportData.summary?.total_difference)}</p>
            <span>Kalan bakiye</span>
          </div>
        </div>
        
        
        <h3 className="section-title">Aylık Karşılaştırma</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Ay</th>
              <th>Sipariş Tutarı</th>
              <th>Tahsilat</th>
              <th>Toplam Alacak</th>
              <th>Tahsilat Oranı</th>
            </tr>
          </thead>
          <tbody>
            {reportData.monthly?.map((item, idx) => {
              const displayRate = Math.min(item.collection_rate, 100);
              const barColor = item.collection_rate >= 100 ? '#4caf50' : item.collection_rate >= 50 ? '#ff9800' : '#f44336';
              
              return (
                <tr key={idx} className={item.order_total === 0 && item.payment_total === 0 ? 'row-muted' : ''}>
                  <td><strong>{item.month_name}</strong></td>
                  <td>{formatCurrency(item.order_total)}</td>
                  <td>{formatCurrency(item.payment_total)}</td>
                  <td className={item.difference > 0 ? 'text-danger' : 'text-success'}>
                    {formatCurrency(item.difference)}
                  </td>
                  <td>
                    <div className="progress-bar-container">
                      <div className="progress-bar" style={{width: `${displayRate}%`, backgroundColor: barColor}}></div>
                      <span style={{color: item.collection_rate > 100 ? '#4caf50' : 'inherit', fontWeight: item.collection_rate > 100 ? '600' : 'normal'}}>
                        {item.collection_rate}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>TOPLAM</strong></td>
              <td><strong>{formatCurrency(reportData.summary?.total_orders)}</strong></td>
              <td><strong>{formatCurrency(reportData.summary?.total_payments)}</strong></td>
              <td className={reportData.summary?.total_difference > 0 ? 'text-danger' : 'text-success'}>
                <strong>{formatCurrency(reportData.summary?.total_difference)}</strong>
              </td>
              <td><strong>{totalRate}%</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderReport = () => {
    switch (activeReport) {
      case 'payment-summary': return renderPaymentSummary();
      case 'monthly-collections': return renderMonthlyCollections();
      case 'kdv-summary': return renderKdvSummary();
      case 'daily-collections': return renderDailyCollections();
      case 'receivables': return renderReceivables();
      case 'comparison': return renderComparison();
      default: return null;
    }
  };

  return (
    <div className="page-container">
      <Navbar />
      
      <div className="page-content">
        <div className="page-header no-print">
          <h2>📊 Finansal Raporlar</h2>
          <button className="btn-primary" onClick={handlePrint}>🖨️ Yazdır</button>
        </div>

        <div className="report-tabs no-print">
          {reportTypes.map(report => (
            <button
              key={report.id}
              className={`report-tab ${activeReport === report.id ? 'active' : ''}`}
              onClick={() => setActiveReport(report.id)}
            >
              <span className="tab-label">{report.label}</span>
              <span className="tab-desc">{report.description}</span>
            </button>
          ))}
        </div>

        {renderFilters()}

        <div className="report-container">
          <div className="print-header">
            <h1>Ekol Tabela ERP</h1>
            <h2>{reportTypes.find(r => r.id === activeReport)?.label}</h2>
            <p>Rapor Tarihi: {new Date().toLocaleDateString('tr-TR')}</p>
          </div>
          
          {loading ? (
            <div className="loading">Rapor yükleniyor...</div>
          ) : (
            renderReport()
          )}
        </div>
      </div>

      <style>{`
        .report-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 20px;
        }
        
        .report-tab {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 12px 20px;
          border: 2px solid #e0e0e0;
          border-radius: 10px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .report-tab:hover {
          border-color: #667eea;
          background: #f8f9ff;
        }
        
        .report-tab.active {
          border-color: #667eea;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        
        .tab-label {
          font-weight: 600;
          font-size: 14px;
        }
        
        .tab-desc {
          font-size: 11px;
          opacity: 0.7;
          margin-top: 2px;
        }
        
        .report-filters {
          display: flex;
          align-items: flex-end;
          gap: 15px;
          padding: 20px;
          background: white;
          border-radius: 12px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        
        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        
        .filter-group label {
          font-size: 12px;
          color: #666;
          font-weight: 500;
        }
        
        .filter-group input,
        .filter-group select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
        }
        
        .filter-group input:disabled,
        .filter-group select:disabled {
          background: #f5f5f5;
          color: #999;
        }
        
        .filter-divider {
          color: #999;
          font-size: 12px;
          padding-bottom: 10px;
        }
        
        .btn-small {
          padding: 6px 12px !important;
          font-size: 12px !important;
        }
        
        .btn-secondary {
          background: #f5f5f5;
          color: #666;
          border: 1px solid #ddd;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .btn-secondary:hover {
          background: #eee;
        }
        
        .report-container {
          background: white;
          border-radius: 12px;
          padding: 30px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        
        .section-title {
          margin-top: 30px;
          margin-bottom: 15px;
          font-size: 16px;
          color: #333;
        }
        
        .report-summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }
        
        .summary-card {
          padding: 20px;
          border-radius: 12px;
          text-align: center;
        }
        
        .summary-card h4 {
          margin: 0 0 10px 0;
          font-size: 14px;
          opacity: 0.9;
        }
        
        .summary-card .big-number {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
        }
        
        .summary-card span {
          font-size: 12px;
          opacity: 0.8;
        }
        
        .summary-card.primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        
        .summary-card.success {
          background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
          color: white;
        }
        
        .summary-card.warning {
          background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
          color: white;
        }
        
        .summary-card.danger {
          background: linear-gradient(135deg, #f44336 0%, #c62828 100%);
          color: white;
        }
        
        .summary-card.info {
          background: linear-gradient(135deg, #2196f3 0%, #1565c0 100%);
          color: white;
        }
        
        .summary-card.secondary {
          background: linear-gradient(135deg, #9e9e9e 0%, #616161 100%);
          color: white;
        }
        
        .report-note {
          background: #fff3e0;
          padding: 12px 16px;
          border-radius: 8px;
          margin-top: 20px;
          font-size: 13px;
          color: #e65100;
          border-left: 4px solid #ff9800;
        }
        
        .report-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .report-table th,
        .report-table td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid #eee;
        }
        
        .report-table th {
          background: #f8f9fa;
          font-weight: 600;
          color: #333;
          font-size: 13px;
        }
        
        .report-table tbody tr:hover {
          background: #f8f9ff;
        }
        
        .report-table tfoot td {
          background: #f0f0f0;
          font-weight: 600;
        }
        
        .row-muted {
          opacity: 0.5;
        }
        
        .empty-row {
          text-align: center;
          padding: 30px !important;
          color: #999;
        }
        
        .text-danger {
          color: #d32f2f;
        }
        
        .text-success {
          color: #2e7d32;
        }
        
        .progress-bar-container {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .progress-bar-container .progress-bar {
          height: 8px;
          background: #667eea;
          border-radius: 4px;
          min-width: 10px;
          max-width: 100px;
        }
        
        .bar-chart {
          height: 20px;
          background: #f0f0f0;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .bar-chart .bar {
          height: 100%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 4px;
          transition: width 0.3s;
        }
        
        .print-header {
          display: none;
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #333;
        }
        
        .print-header h1 {
          margin: 0;
          font-size: 24px;
        }
        
        .print-header h2 {
          margin: 10px 0 5px 0;
          font-size: 18px;
          color: #666;
        }
        
        .print-header p {
          margin: 0;
          color: #999;
          font-size: 12px;
        }
        
        .loading {
          text-align: center;
          padding: 50px;
          color: #999;
          font-size: 16px;
        }
        
        @media print {
          .no-print {
            display: none !important;
          }
          
          .page-container {
            padding: 0;
          }
          
          .report-container {
            box-shadow: none;
            padding: 0;
          }
          
          .print-header {
            display: block;
          }
          
          .summary-card {
            border: 1px solid #ccc;
            color: #333 !important;
            background: white !important;
          }
          
          .summary-card .big-number {
            color: #333;
          }
        }
      `}</style>
    </div>
  );
}

export default Reports;